import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { useDropzone } from 'react-dropzone';
import { shallowEqual, useSelector } from 'react-redux';
import {
  CircularProgress,
  makeStyles,
  Theme,
  useTheme,
  useMediaQuery
} from '@material-ui/core';
import { RootState } from '@modules/index';
import { UploadedReferenceFileType } from '@modules/request';
import useFiles, { UploadedFileReturnType } from '@hooks/useFiles';
import useRequest from '@hooks/useRequest';
import useQueryString from '@hooks/useQueryString';
import {
  CapaPcbServiceType,
  CapaQuotationRfqAssemblyFile,
  CapaQuotationRfqFabricationFile,
  CapaQuotationRfqReferenceFile,
  CapaQuotationServiceType,
  RegisterRfqInput,
  RegisterRfqPcbInput,
  RfqPcbAssemblyFileInput,
  RfqPcbFabricationFileInput,
  RfqReferenceFileInput
} from '@generated/graphql';
import { colorSet, useSnackbar } from '@components/Provider';
import { CustomizedButton, CustomizedTypography } from '@components/Customized';
import UploadedFileListEntry from './UploadedFileListEntry';
import ReferenceFileListEntry from './ReferenceFileListEntry';
import { RequestQueryString } from '../types';
import DesignReferenceFileListEntry from './DesignReferenceFileListEntry';

interface Props {
  serviceType: CapaQuotationServiceType;
  acceptFileFormats?: string;
  type: 'designReference' | 'reference' | 'drawing' | 'gerber' | 'BOM';
  sizeLimitMB: number;
  quantityLimit: number;
  icon: React.ReactNode;
  title: React.ReactNode;
  description: React.ReactNode;
  onDragText?: React.ReactNode;
  error?: string;
  filesRef?: React.MutableRefObject<any[]>;
}

interface DropzoneStyleProps {
  isDragActive: boolean;
  hasUploadedFiles: boolean;
  type: 'designReference' | 'reference' | 'drawing' | 'gerber' | 'BOM';
}

export interface UploadedFileWithProperty
  extends UploadedFileReturnType,
    CapaQuotationRfqReferenceFile,
    Omit<CapaQuotationRfqAssemblyFile, '__typename' | 'quantity'>,
    Omit<
      CapaQuotationRfqFabricationFile,
      '__typename' | 'quantity' | 'thickness'
    >,
    UploadedReferenceFileType {
  quantity?: string;
  thickness?: string;
  fileSize?: number | null;
}

const typeToKor = {
  'designReference': '참고 파일',
  'reference': '참고 파일',
  'drawing': '도면 파일',
  'gerber': '거버 파일',
  'BOM': 'BOM 파일'
};

const RequestFormFileInput: React.FC<Props> = ({
  acceptFileFormats,
  sizeLimitMB,
  quantityLimit,
  type,
  icon,
  title,
  onDragText,
  description,
  error,
  filesRef
}) => {
  const uploadedReferenceFiles = useSelector<
    RootState,
    RfqReferenceFileInput[]
  >((state) => state.request?.referenceFiles ?? []);
  const pcb = useSelector<RootState, RegisterRfqPcbInput>(
    (state) => state.request?.pcb ?? { services: [] },
    shallowEqual
  );
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { onUploadFiles } = useFiles();
  const {
    onUpdateCircuitFabricationFile,
    onUpdateCircuitAssemblyFile,
    onUpdateRfqInfo
  } = useRequest();
  const { control } = useFormContext<RegisterRfqInput>();
  const {
    query: { process }
  } = useQueryString<RequestQueryString>();
  const { fabricationFiles, assemblyFiles } = pcb;
  const [uploadedFiles, setUploadedFile] = useState<UploadedFileWithProperty[]>(
    type === 'reference' || type === 'designReference'
      ? uploadedReferenceFiles ?? []
      : type === 'BOM'
      ? (assemblyFiles ?? []).map(
          (file) =>
            ({
              ...file,
              quantity: String(file?.quantity) ?? undefined
            } as UploadedFileWithProperty)
        )
      : type === 'gerber'
      ? (fabricationFiles ?? []).map(
          (file) =>
            ({
              ...file,
              thickness: String(file.thickness ?? undefined),
              quantity: String(file?.quantity ?? undefined)
            } as UploadedFileWithProperty)
        )
      : []
  );

  const typeToFieldName = useMemo(() => {
    switch (type) {
      case 'designReference':
        return 'referenceFiles';
      case 'reference':
        return 'referenceFiles';
      case 'gerber':
        return 'pcb.fabricationFiles';
      case 'BOM':
        return 'pcb.assemblyFiles';
      case 'drawing':
        return 'drawingFiles';
    }
  }, [type]);

  const assemblyOnly =
    (process ?? '').includes(CapaPcbServiceType.CircuitAssembly) &&
    !(process ?? '').includes(CapaPcbServiceType.CircuitFabrication);

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    noClick: true,
    accept: acceptFileFormats,
    onDrop: async (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;

      const overSizeFiles = acceptedFiles?.filter(
        (file) => file.size > sizeLimitMB * 1024 * 1024
      ).length;

      if (overSizeFiles > 0) {
        return enqueueSnackbar(
          `${typeToKor[type]}은 1개당 ${sizeLimitMB}MB 이하의 파일만 업로드 가능합니다.`,
          { variant: 'error' }
        );
      }

      if (uploadedFiles.length + acceptedFiles.length > quantityLimit) {
        return enqueueSnackbar(
          `${typeToKor[type]}은 ${quantityLimit}개까지 업로드 가능합니다.`,
          { variant: 'error' }
        );
      }

      setLoading(true);

      const gerberCheckedFiles = acceptedFiles.map((file) => {
        let name = file.name;
        if (type === 'gerber') {
          if (file.type.includes('zip')) {
            const [fileName] = file.name.split('.zip');
            name = `${fileName}_ateamgerber.zip`;
          }

          if (file.name.includes('.rar')) {
            const [fileName] = file.name.split('.rar');
            name = `${fileName}_ateamgerber.rar`;
          }
          const newFile = new File([file], name, {
            type: name.includes('.zip') ? 'application/zip' : ''
          });

          return newFile;
        } else {
          return file;
        }
      });

      const res = await onUploadFiles(gerberCheckedFiles);

      if (res.error) {
        setLoading(false);
        return enqueueSnackbar(`파일 업로드 실패`, { variant: 'error' });
      }

      if (!res.error) {
        setUploadedFile((prev) =>
          [...prev, ...res.files].map((file: UploadedFileWithProperty) => {
            file.fileName = file.fileName.replace('_ateamgerber', '');

            return file;
          })
        );
        setLoading(false);
        return enqueueSnackbar('파일 업로드 완료', { variant: 'success' });
      }
    },
    onDropRejected: (rejectedFiles) => {
      rejectedFiles.forEach((file) => {
        enqueueSnackbar('지원하지 않는 형식입니다.');
      });
    }
  });
  const {
    uploadInputContainer,
    referenceFileInput,
    uploadedFileList
  } = useStyles({
    isDragActive,
    hasUploadedFiles: Boolean(uploadedFiles.length),
    type
  });

  const isQuantityMax = uploadedFiles.length === quantityLimit;

  const onClickDeleteFile = useCallback((fileId: string) => {
    setUploadedFile((prev) => prev.filter((file) => file.fileId !== fileId));
  }, []);

  const onUpdateFile = useCallback(
    (fileId: string, updateData: Partial<UploadedFileReturnType>) => {
      setUploadedFile((prev) => {
        return prev.map((file) => {
          if (file.fileId === fileId) {
            return (file = { ...file, ...updateData });
          }
          return file;
        });
      });
    },
    []
  );

  useEffect(() => {
    if (type === 'gerber') {
      const gerberConvertedFiles = uploadedFiles.map(
        (file) =>
          ({
            fileId: file.fileId,
            fileName: file.fileName,
            quantity: Number(file.quantity),
            thickness: Number(file.thickness)
          } as RfqPcbFabricationFileInput)
      );

      if (filesRef?.current) {
        filesRef.current = gerberConvertedFiles;
      }

      onUpdateCircuitFabricationFile(gerberConvertedFiles);
    }

    if (type === 'BOM') {
      const bomConvertedFiles = uploadedFiles.map(
        (file) =>
          ({
            fileId: file.fileId,
            fileName: file.fileName,
            quantity: Number(file.quantity)
          } as RfqPcbAssemblyFileInput)
      );

      if (filesRef?.current) {
        filesRef.current = bomConvertedFiles;
      }

      onUpdateCircuitAssemblyFile(bomConvertedFiles);
    }

    if (type === 'reference' || type === 'designReference') {
      const referenceConvertedFiles = uploadedFiles.map((file) => ({
        fileId: file.fileId,
        fileName: file.fileName
      }));
      if (filesRef?.current) {
        filesRef.current = referenceConvertedFiles;
      }

      onUpdateRfqInfo({
        referenceFiles: referenceConvertedFiles
      });
    }

    //eslint-disable-next-line
  }, [
    uploadedFiles,
    onUpdateCircuitFabricationFile,
    onUpdateCircuitAssemblyFile,
    type
  ]);

  return (
    <div className={uploadInputContainer} {...getRootProps()}>
      <input id={type} {...getInputProps()} data-cy={typeToFieldName} />
      <Controller
        control={control}
        name={typeToFieldName}
        defaultValue={[]}
        rules={{
          validate: {
            isEmpty: () =>
              (type === 'gerber' || type === 'BOM') &&
              uploadedFiles.length === 0
                ? `업로드한 ${typeToKor[type]}이 없습니다. ${typeToKor[type]}을 업로드 해주세요.`
                : true,
            allFilesHasProperty: () => {
              if (type === 'gerber') {
                return fabricationFiles?.every(
                  (file) =>
                    Number(file.quantity ?? 0) > 0 &&
                    Number(file.thickness ?? 0) > 0
                )
                  ? true
                  : '두께와 수량을 입력해주세요';
              }

              if (type === 'BOM') {
                return assemblyFiles?.every(
                  (file) => Number(file.quantity ?? 0) > 0
                )
                  ? true
                  : '수량을 입력해주세요';
              }
            }
          }
        }}
        render={() => (
          <input hidden name={typeToFieldName} data-cy={`rfq-upload-${type}`} />
        )}
      />

      <div className={uploadedFileList}>
        {!matches &&
          (type === 'gerber' || type === 'BOM') &&
          uploadedFiles.length > 0 && (
            <div className="column-description">
              <div className="column-name filename">파일명</div>

              <div className="column-name-right">
                <div className="column-name">
                  {type === 'gerber' && !assemblyOnly ? '두께' : ''}
                </div>
                {type === 'BOM' ||
                  (type === 'gerber' && !assemblyOnly && (
                    <div className="column-name">수량</div>
                  ))}
              </div>
            </div>
          )}
        {uploadedFiles.map((file) => {
          if (type === 'reference') {
            return (
              <ReferenceFileListEntry
                key={file.fileId}
                {...file}
                onClickDeleteFile={onClickDeleteFile}
                onUpdateFile={onUpdateFile}
              />
            );
          }

          if (type === 'designReference') {
            return (
              <DesignReferenceFileListEntry
                key={file.fileId}
                {...file}
                onClickDeleteFile={onClickDeleteFile}
                onUpdateFile={onUpdateFile}
              />
            );
          }

          return (
            <UploadedFileListEntry
              key={file.fileId}
              {...file}
              type={type}
              onClickDeleteFile={onClickDeleteFile}
              onUpdateFile={onUpdateFile}
            />
          );
        })}
        {!isQuantityMax && (
          <div className={referenceFileInput}>
            {!loading ? (
              <>
                {uploadedFiles.length < 1 && (
                  <>
                    {icon}
                    {isDragActive && onDragText ? onDragText : title}
                    {!isDragActive && description}
                  </>
                )}
                {(!isDragActive || uploadedFiles.length > 0) && (
                  <CustomizedButton
                    variant="text"
                    color="primary"
                    onClick={open}
                    className="input-trigger"
                  >
                    <CustomizedTypography
                      variant="inherit"
                      color={!isDragActive ? 'inherit' : 'textPrimary'}
                    >
                      {!isDragActive ? '기기에서 업로드' : '끌어다 놓으세요'}
                    </CustomizedTypography>
                  </CustomizedButton>
                )}
              </>
            ) : (
              <CircularProgress color="primary" />
            )}
          </div>
        )}
      </div>
      {error && (
        <CustomizedTypography
          variant="caption"
          color="error"
          className="helperText"
        >
          {error}
        </CustomizedTypography>
      )}
    </div>
  );
};

export default React.memo(RequestFormFileInput);

const useStyles = makeStyles<Theme, DropzoneStyleProps>((theme) => ({
  uploadInputContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    '& .helperText': {
      margin: '-5px 14px'
    }
  },
  uploadedFileList: {
    display: 'flex',
    justifyContent: 'space-between',
    alignContent: ({ type }) =>
      type === 'reference' ? 'flex-end' : 'flex-start',
    flexWrap: 'wrap',
    gap: '8px',
    '& .column-description': {
      display: 'flex',
      justifyContent: 'space-between',
      width: '100%',
      marginTop: '4px',
      '& .column-name': {
        width: '100%',
        maxWidth: '110px',
        ...theme.typography.subtitle2,
        color: theme.palette.text.secondary,
        '&.filename': {
          maxWidth: 'unset',
          width: 'unset'
        }
      },
      '& .column-name-right': {
        width: '260px',
        paddingRight: '48px',
        display: 'flex',
        gap: '4px'
      }
    },
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      maxWidth: 'unset'
    }
  },
  referenceFileInput: {
    width: '100%',
    border: ({ isDragActive }) =>
      !isDragActive
        ? `1px dashed ${colorSet.gray600}`
        : `2px solid ${colorSet.gray900}`,
    backgroundColor: ({ isDragActive }) =>
      !isDragActive ? '' : colorSet.gray100,
    borderRadius: '8px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ({ hasUploadedFiles, type, isDragActive }) =>
      (type === 'reference' || type === 'designReference') && hasUploadedFiles
        ? 'unset'
        : isDragActive
        ? 31
        : 32,
    maxWidth: ({ hasUploadedFiles, type }) =>
      type !== 'reference' && type !== 'designReference'
        ? 'unset'
        : hasUploadedFiles
        ? 228
        : 'unset',
    '& img': {
      marginBottom: 12
    },
    height: ({ hasUploadedFiles, type }) =>
      hasUploadedFiles
        ? type === 'designReference'
          ? '56px'
          : '48px'
        : type === 'reference'
        ? '150px'
        : '248px',
    [theme.breakpoints.down('sm')]: {
      maxWidth: () => 'unset'
    }
  }
}));
